import apiClient from './axiosConfig';
import { getAccessToken, isTokenExpired } from './authToken';
import { refreshAuthTokens } from './authSession';
import { maybeStartSessionSync } from './sessionSync';
import {
  clearCachedAccessStatus,
  readCachedAccessStatus,
  writeCachedAccessStatus,
} from './accessStatusCache';
import { applyRuntimeDormantFeatures, getDormantFeatures } from "./dormantFeatures.js";
import { applyRuntimeOptionsTrust, optionsTrustEnabled } from "./optionsTrustFlag.js";
import { applyRuntimeSurveyOneTapQ1, surveyOneTapQ1Enabled } from "./surveyOneTapFlag";
import {
  decideNextPath as decidePostAuthPath,
  hasTripConnectResume,
  carryAuthAttribution,
  incompleteSurveyPath,
  isAllowedWithinRequiredPath as isAllowedPostAuthPath,
  readSameOriginNext,
  signedOutRedirectFor,
} from './postAuthPath';
import { applyRuntimeReengage, reengageEnabled } from "./reengageFlag";
import { readPreItineraryConnect } from './tripPreItineraryConnect';
import { isPublicAppPath } from './publicPaths';
import { withIncomingSearch } from './landingQuery';
import { isInactiveAccessError } from './inactiveLogin';

export { resolvePostAuthDestination } from './postAuthPath';

const POST_AUTH_ENTRY_PATHS = new Set([
  '/',
  '/login',
  '/signup',
  '/oauth/callback',
  '/stripe/success',
  '/survey',
]);

let latestRouteDecisionId = 0;
let accessStatusRequestInFlight = null;
const ONBOARDING_STATUS_CACHE_KEY = 'dople_onboarding_completion';

export function readCachedOnboardingCompletion() {
  try {
    const value = window.localStorage.getItem(ONBOARDING_STATUS_CACHE_KEY);
    if (value === 'complete') return true;
    if (value === 'incomplete') return false;
  } catch {
    // Cache is an optimization only; the backend remains authoritative.
  }
  return null;
}

export function clearCachedOnboardingCompletion() {
  try {
    window.localStorage.removeItem(ONBOARDING_STATUS_CACHE_KEY);
  } catch {
    // Ignore unavailable storage.
  }
  clearCachedAccessStatus();
}

// After /api/onboarding/complete succeeds, refresh local routing caches immediately
// so FlowGuard cannot send the user back to /survey before check_access round-trips.
export function markOnboardingSurveyCompletedLocally(statusPatch = {}) {
  try {
    window.localStorage.setItem(ONBOARDING_STATUS_CACHE_KEY, 'complete');
  } catch {
    // Navigation still works if storage is unavailable.
  }
  const token = getAccessToken() || '';
  const cached = readCachedAccessStatus({ token }) || {};
  writeCachedAccessStatus(
    {
      ...cached,
      surveyCompleted: true,
      appAccessGranted: true,
      requiresSurvey: false,
      nextStep: statusPatch.nextStep || cached.nextStep || 'trip_first',
      nextPath: statusPatch.nextPath || cached.nextPath || '/trips',
      ...statusPatch,
    },
    { token }
  );
}

export function readCachedStatus() {
  const status = readCachedAccessStatus({ token: getAccessToken() || "" });
  if (status?.features) {
    applyRuntimeDormantFeatures(status.features);
    applyRuntimeOptionsTrust(status.features);
    applyRuntimeSurveyOneTapQ1(status.features);
    applyRuntimeReengage(status.features);
  }
  return status;
}

/* ------------------------------------------------------------------ */
/* Utils */
/* ------------------------------------------------------------------ */

async function getWithTimeout(url, { timeoutMs = 8000 } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await apiClient.get(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function retry(fn, { tries = 2, delayMs = 300 } = {}) {
  let lastErr;

  for (let i = 0; i < tries; i++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      if (i < tries - 1) {
        await new Promise(r => setTimeout(r, delayMs * (i + 1)));
      }
    }
  }

  throw lastErr;
}

function isDefinitiveRefreshAuthFailure(error) {
  const status = Number(error?.response?.status || 0);
  if (status === 401) return true;
  const detail = `${error?.response?.data?.detail || ''} ${error?.response?.data?.code || ''}`.toLowerCase();
  return detail.includes('invalid refresh') || detail.includes('token_not_valid');
}

/* ------------------------------------------------------------------ */
/* Wait for JWT */
/* ------------------------------------------------------------------ */

export function waitForAccessToken({
  timeoutMs = 4000,
  intervalMs = 50
} = {}) {
  return new Promise(resolve => {
    const start = Date.now();
    const t = setInterval(() => {
      const token = getAccessToken();

      if (token) {
        clearInterval(t);
        resolve(token);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(t);
        resolve(null);
      }
    }, intervalMs);
  });
}

/* ------------------------------------------------------------------ */
/* Fetch Full Onboarding Status */
/* ------------------------------------------------------------------ */

export async function fetchStatus({
  timeoutMs = 8000,
  retries = 1,
  useCache = false,
} = {}) {
  if (useCache) {
    const cachedStatus = readCachedStatus();
    if (cachedStatus) return cachedStatus;
  }
  if (accessStatusRequestInFlight) return accessStatusRequestInFlight;
  accessStatusRequestInFlight = (async () => {
    const doFetch = () => getWithTimeout("/api/check_access", { timeoutMs });
    const access = await retry(doFetch, {
      tries: Math.max(1, retries),
      delayMs: 300
    });
    const data = access?.data || {};
    if (data.features) {
      applyRuntimeDormantFeatures(data.features);
      applyRuntimeOptionsTrust(data.features);
      applyRuntimeSurveyOneTapQ1(data.features);
      applyRuntimeReengage(data.features);
    } else {
      try {
        const contextResponse = await apiClient.get("/api/app-context");
        if (contextResponse?.data?.features) {
          applyRuntimeDormantFeatures(contextResponse.data.features);
          applyRuntimeOptionsTrust(contextResponse.data.features);
          applyRuntimeSurveyOneTapQ1(contextResponse.data.features);
          applyRuntimeReengage(contextResponse.data.features);
        }
      } catch {
        // Trip-first accounts cannot read app-context. Build-time flags stay in effect.
      }
    }
    if (Object.prototype.hasOwnProperty.call(data, "survey_onetap_q1")) {
      applyRuntimeSurveyOneTapQ1({ survey_onetap_q1: data.survey_onetap_q1 });
    }

    try {
      window.localStorage.setItem(
        ONBOARDING_STATUS_CACHE_KEY,
        data.survey_completed ? 'complete' : 'incomplete'
      );
    } catch {
      // Cache is an optimization only; routing still uses this response.
    }

    const status = {
      tier: data.tier || "free",
      rawTier: data.raw_tier || data.tier || "free",
      surveyCompleted: Boolean(data.survey_completed),
      hasSubscription: Boolean(data.has_subscription),
      hasPlaid: Boolean(data.has_plaid),
      hasModel: Boolean(data.has_model),
      requiresPlaid: Boolean(data.requires_plaid),
      requiresModel: Boolean(data.requires_model),
      appAccessGranted: Boolean(data.app_access_granted),
      nextStep: data.next_step || "app",
      nextPath: data.next_path || "/trips",
      features: {
        ...getDormantFeatures(),
        ...(data.features && typeof data.features === "object" ? data.features : {}),
        options_trust: optionsTrustEnabled(),
        survey_onetap_q1: surveyOneTapQ1Enabled(),
        reengage_enabled: reengageEnabled(),
      },
      entitlements: data.entitlements || {},
      membershipActive: Boolean(
        data.entitlements?.membership_active ||
          (data.has_subscription && ["core", "premium"].includes(String(data.tier || "").toLowerCase()))
      ),
      membershipCurrentPeriodEnd: data.membership_current_period_end || null,
    };
    writeCachedAccessStatus(status, { token: getAccessToken() || "" });
    return status;
  })();
  try {
    return await accessStatusRequestInFlight;
  } finally {
    accessStatusRequestInFlight = null;
  }
}

async function resolveAccessTokenAfterAuth() {
  let token = getAccessToken();
  if (token && !isTokenExpired(token, 30)) {
    return token;
  }

  // Child route guards can mount before Layout's authentication effects run.
  // Give that bootstrap a brief opportunity to restore an access token, but
  // never use an expired token to make the onboarding routing decision.
  if (!token) {
    token = await waitForAccessToken({ timeoutMs: 1200, intervalMs: 50 });
    if (token && !isTokenExpired(token, 30)) return token;
  }

  try {
    await refreshAuthTokens();
  } catch (error) {
    if (!isDefinitiveRefreshAuthFailure(error)) {
      throw error;
    }
    // A refresh cookie/session can finish propagating while concurrent app
    // bootstrap calls are settling. Do one grace check before showing Login.
    await new Promise(resolve => setTimeout(resolve, 500));
    const restoredToken = getAccessToken();
    if (restoredToken && !isTokenExpired(restoredToken, 30)) {
      return restoredToken;
    }
    try {
      await refreshAuthTokens();
    } catch (retryError) {
      if (!isDefinitiveRefreshAuthFailure(retryError)) throw retryError;
      return null;
    }
  }
  token = await waitForAccessToken({ timeoutMs: 1200, intervalMs: 50 });
  return token && !isTokenExpired(token, 30) ? token : null;
}

/* ------------------------------------------------------------------ */
/* Canonical Onboarding Order */
/* ------------------------------------------------------------------ */

export function decideNextPath(status) {
  return decidePostAuthPath(status);
}

// Incomplete accounts land on /survey. A same-origin next on the page they
// are leaving keeps ref, step, and attribution (cta_source, click ids, utm).
// The page query is merged the same way. The allowed-path check still uses /survey.
function surveyNavigationPath(requiredPath, search) {
  if (requiredPath !== "/survey") return requiredPath;
  const surveyPath = incompleteSurveyPath(readSameOriginNext(search));
  return carryAuthAttribution(surveyPath, search) || surveyPath;
}

// Status fetch failed after OAuth already issued a token. Only a subject-matched
// access-status cache may leave /survey; otherwise the user may still be incomplete.
export function pathAfterOAuthStatusFailure() {
  const cachedStatus = readCachedStatus();
  if (cachedStatus) return decideNextPath(cachedStatus);
  return "/survey";
}

function isAllowedWithinRequiredPath(currentPath, requiredPath) {
  let tripConnectResume = false;
  if (currentPath === "/connect" && requiredPath === "/trips") {
    let search = "";
    try {
      search = window.location.search || "";
    } catch {
      search = "";
    }
    tripConnectResume = hasTripConnectResume(search, readPreItineraryConnect()?.tripId);
  }
  return isAllowedPostAuthPath(currentPath, requiredPath, { tripConnectResume });
}

/* ------------------------------------------------------------------ */
/* Router Guard */
/* ------------------------------------------------------------------ */

export async function routeToNextStep(navigate, opts = {}) {
  const routeDecisionId = ++latestRouteDecisionId;
  const {
    replace = true,
    retries = 1,
    timeoutMs = 8000,
    silent = false,
    currentPath = window.location.pathname,
    forceResolvedPath = false,
  } = opts;
  const search = opts.search !== undefined ? opts.search : (window.location.search || "");
  const hash = opts.hash !== undefined ? opts.hash : (window.location.hash || "");
  const signedOutLogin = () => signedOutRedirectFor(currentPath, search, hash);
  const go = (path, options) => navigate(withIncomingSearch(path, search, hash), options);
  const hasUsableAccessToken = () => {
    const token = getAccessToken();
    return Boolean(token) && !isTokenExpired(token, 30);
  };

  try {
    const token = await resolveAccessTokenAfterAuth();

    /* --------------------------------------------------
       No token → allow login/public routes
    -------------------------------------------------- */
    if (!token) {
      // Never flash Login if another bootstrap path restored the token between
      // resolution and navigation.
      const lateToken = getAccessToken();
      if (lateToken && !isTokenExpired(lateToken, 30)) {
        const status = await fetchStatus({ timeoutMs, retries: Math.max(retries, 2) });
        const requiredPath = decideNextPath(status);
        const navigationPath = surveyNavigationPath(requiredPath, search);
        const allowedHere = isAllowedWithinRequiredPath(currentPath, requiredPath);
        if (!silent && routeDecisionId === latestRouteDecisionId && !allowedHere) {
          go(navigationPath, { replace: true });
        }
        return { next: allowedHere ? requiredPath : navigationPath, status };
      }
      // FlowGuard lets public pages render signed out. Auth flows that force a
      // resolved path (OAuth, Stripe success, activation, login) still go to login.
      if (!forceResolvedPath && isPublicAppPath(currentPath)) {
        return { next: currentPath, status: {} };
      }
      const loginPath = signedOutLogin();
      if (!silent && routeDecisionId === latestRouteDecisionId && currentPath !== "/login") {
        go(loginPath, { replace: true });
      }
      return { next: loginPath, status: {} };
    }

    const statusRetries = currentPath === "/oauth/callback"
      ? Math.max(retries, 3)
      : retries;
    const shouldRefreshStatus = forceResolvedPath || POST_AUTH_ENTRY_PATHS.has(currentPath);
    const status = await fetchStatus({
      timeoutMs,
      retries: statusRetries,
      useCache: !shouldRefreshStatus,
    });
    const requiredPath = decideNextPath(status);
    const navigationPath = surveyNavigationPath(requiredPath, search);

    // Kick off one background financial refresh per session once the user
    // is fully in-app. This does not block navigation.
    maybeStartSessionSync(status);

    /* --------------------------------------------------
      If onboarding is incomplete → force forward only
    -------------------------------------------------- */
    const onboardingIncomplete = !status.appAccessGranted || !status.surveyCompleted;

    const isCallback = currentPath === "/oauth/callback";
    const postAuthRedirectPaths = POST_AUTH_ENTRY_PATHS;

    if (forceResolvedPath && !isAllowedWithinRequiredPath(currentPath, requiredPath)) {
      if (!silent && routeDecisionId === latestRouteDecisionId) {
        go(navigationPath, { replace });
      }
      return { next: navigationPath, status };
    }

    if ((onboardingIncomplete || isCallback) && !isAllowedWithinRequiredPath(currentPath, requiredPath)) {      
      if (!silent && routeDecisionId === latestRouteDecisionId) {
        go(navigationPath, { replace });
      }
      return { next: navigationPath, status };
    }

    /* --------------------------------------------------
      If onboarding complete → allow free navigation
    -------------------------------------------------- */
    if (postAuthRedirectPaths.has(currentPath) && currentPath !== requiredPath) {
      if (!silent && routeDecisionId === latestRouteDecisionId) {
        go(navigationPath, { replace });
      }
      return { next: navigationPath, status };
    }

    return { next: currentPath, status };

  } catch (e) {
    // A never-verified account gets check_access 401 account_inactive. On the
    // OAuth callback that page shows "verify your email", so stay there instead
    // of sending a session with a token on to /survey (which then logs out to
    // /signup).
    if (currentPath === "/oauth/callback" && isInactiveAccessError(e)) {
      return { next: currentPath, status: {}, inactive: true };
    }
    console.error('[routeToNextStep] failed:', e);
    if (!hasUsableAccessToken()) {
      if (!forceResolvedPath && isPublicAppPath(currentPath)) {
        return { next: currentPath, status: {} };
      }
      const loginPath = signedOutLogin();
      if (!silent && routeDecisionId === latestRouteDecisionId && currentPath !== "/login") {
        go(loginPath, { replace: true });
      }
      return { next: loginPath, status: {} };
    }
    if (POST_AUTH_ENTRY_PATHS.has(currentPath) && getAccessToken()) {
      const fallbackPath = surveyNavigationPath(pathAfterOAuthStatusFailure(), search);
      if (!silent && routeDecisionId === latestRouteDecisionId) {
        go(fallbackPath, { replace: true });
      }
      return { next: fallbackPath, status: {} };
    }
    return { next: currentPath, status: {} };
  }
}

export async function waitForBackendAuth({
  timeoutMs = 4000,
  intervalMs = 200
} = {}) {
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    try {
      const res = await apiClient.get("/api/auth/ready");

      if (res?.data?.ready) {
        return true;
      }
    } catch (e) {
      // ignore errors, just retry
    }

    await new Promise(r => setTimeout(r, intervalMs));
  }

  return false;
}

